// MarketingPerformancePanel.jsx — v119.124
// Weekly performance dashboard for marketing operations. Renders inside
// Campaigns page (top slot) so every marketing/admin login sees the
// prior-week snapshot + actionable late-task nudges.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./lib/api";
import { showToast } from "./lib/theme";

function KpiTile({ icon, label, value, tone = "neutral", testid }) {
  const bg = tone === "success" ? "#dcfce7" :
             tone === "warn"    ? "#fef3c7" :
             tone === "danger"  ? "#fee2e2" : "#fff";
  const fg = tone === "success" ? "#166534" :
             tone === "warn"    ? "#92400e" :
             tone === "danger"  ? "#991b1b" : "#3E2723";
  return (
    <div data-testid={testid} style={{
      background: bg, padding: "10px 14px", borderRadius: 10,
      minWidth: 130, flex: 1, border: `1px solid ${fg}20`,
    }}>
      <div style={{ fontSize: 11, color: fg, fontWeight: 700, opacity: 0.75 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color: fg, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

export default function MarketingPerformancePanel() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [radar, setRadar] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [radarSending, setRadarSending] = useState(false);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(false);
  const [nudgingId, setNudgingId] = useState(null);
  // v119.127 — dedupe strict-mode double-mount (impact-history was firing twice).
  const loadedOnce = useRef(false);
  const meLoadedOnce = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, h] = await Promise.all([
        api.get("/marketing-plans/performance/weekly"),
        api.get("/marketing-plans/performance/impact-history?months=6"),
      ]);
      setData(w.data);
      setHistory(h.data.months || []);
    } catch (_e) { /* silent — role may not permit */ }
    setLoading(false);
  }, []);
  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    load();
  }, [load]);
  useEffect(() => {
    if (meLoadedOnce.current) return;
    meLoadedOnce.current = true;
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const runRadar = async () => {
    setRadarLoading(true);
    try {
      const r = await api.get("/marketing-plans/risk-radar");
      setRadar(r.data);
      showToast(`🎯 حلّل ${r.data.total_risks} إشارة خطر`, r.data.total_risks > 0 ? "warn" : "success");
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل التحليل", "danger");
    }
    setRadarLoading(false);
  };

  const sendRadarDigest = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("إرسال ملخّص رادار المخاطر عبر واتساب لكل هواتف المدراء المسجّلة الآن؟")) return;
    setRadarSending(true);
    try {
      const r = await api.post("/marketing-plans/risk-radar/send-digest");
      const s = r.data.sent?.length || 0;
      const f = r.data.failed?.length || 0;
      showToast(`📤 أُرسل: ${s} · فشل: ${f} · إشارات: ${r.data.total_risks}`,
                  f > 0 ? "warn" : "success");
      setRadar({
        generated_at: new Date().toISOString(),
        total_risks: r.data.total_risks,
        signals: {}, ai_summary: r.data.preview_text,
      });
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل إرسال الملخّص", "danger");
    }
    setRadarSending(false);
  };

  const nudgeOne = async (tid) => {
    setNudgingId(tid);
    try {
      const r = await api.post(`/marketing-plans/performance/nudge/${tid}`);
      showToast(`✅ أُرسل التذكير إلى ${r.data.to}`, "success");
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل الإرسال", "danger");
    }
    setNudgingId(null);
  };

  const nudgeAll = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("إرسال تذكيرات واتساب لكل الموظفين المتأخرين أو المتعثّرين؟")) return;
    setNudging(true);
    try {
      const r = await api.post("/marketing-plans/performance/nudge-all-late");
      const s = r.data.sent?.length || 0;
      const f = r.data.failed?.length || 0;
      showToast(`📤 أُرسل: ${s} · فشل: ${f} · إجمالي المهام: ${r.data.total_late_tasks}`,
                  f > 0 ? "warn" : "success");
      load();
    } catch (e) {
      showToast(e?.response?.data?.detail || "فشل الإرسال", "danger");
    }
    setNudging(false);
  };

  if (loading) return null;
  if (!data) return null;
  const k = data.kpi || {};

  return (
    <div data-testid="mkt-performance-panel" style={{ marginBottom: 20 }}>
      <div style={{
        padding: "12px 16px", borderRadius: 10, marginBottom: 10,
        background: "linear-gradient(135deg, #3E2723 0%, #5D4037 60%, #C9A227 120%)",
        color: "#FBF7F0",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: 2 }}>WEEKLY MARKETING PERFORMANCE</div>
            <div style={{ fontSize: 15, fontWeight: 900 }}>
              📊 لوحة الأداء الأسبوعي
              <span style={{ fontSize: 11, marginInlineStart: 8, opacity: 0.9 }}>
                ({data.period_start} → {data.period_end})
              </span>
            </div>
          </div>
          {(k.stuck_count > 0 || k.overdue_count > 0) && (
            <button type="button" onClick={nudgeAll} disabled={nudging}
              data-testid="mkt-perf-nudge-all"
              style={{ padding: "8px 16px", background: "#C9A227", color: "#3E2723",
                        border: "none", borderRadius: 6, fontWeight: 800, fontSize: 12,
                        cursor: nudging ? "wait" : "pointer" }}>
              {nudging ? "⏳ يرسل..." : `📱 تذكير كل المتأخرين (${k.stuck_count + k.overdue_count})`}
            </button>
          )}
        </div>
      </div>

      {/* KPI Tiles */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <KpiTile testid="mkt-perf-kpi-completion" icon="✅" label="نسبة الإنجاز"
          value={k.no_tasks ? (k.completion_label || "بلا مهام") : `${k.completion_pct}%`}
          tone={k.no_tasks ? "warn" : (k.completion_pct >= 80 ? "success" : k.completion_pct >= 50 ? "warn" : "danger")} />
        <KpiTile testid="mkt-perf-kpi-impact" icon="🎯" label="متوسط الأثر"
          value={k.avg_impact_pct != null ? `${k.avg_impact_pct}%` : "—"}
          tone={k.avg_impact_pct == null ? "neutral" : k.avg_impact_pct >= 80 ? "success" : "warn"} />
        <KpiTile testid="mkt-perf-kpi-stuck" icon="⏸️" label="معلّقة > 3 أيام"
          value={k.stuck_count} tone={k.stuck_count > 0 ? "warn" : "success"} />
        <KpiTile testid="mkt-perf-kpi-overdue" icon="⏰" label="متأخرة"
          value={k.overdue_count} tone={k.overdue_count > 0 ? "danger" : "success"} />
        <KpiTile testid="mkt-perf-kpi-unmeasured" icon="📉" label="حملات بلا قياس"
          value={k.unmeasured_camp} tone={k.unmeasured_camp > 0 ? "warn" : "success"} />
      </div>

      {/* Late/Stuck Tasks List */}
      {(data.stuck?.length > 0 || data.overdue?.length > 0) && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: "var(--brown-deep)", marginBottom: 8 }}>
            🚧 مهام تحتاج انتباه
          </div>
          {[...(data.overdue || []).map((x) => ({ ...x, _kind: "overdue" })),
              ...(data.stuck   || []).map((x) => ({ ...x, _kind: "stuck"   })),
             ].slice(0, 15).map((t) => (
            <div key={t.id} data-testid={`mkt-perf-late-${t.id}`} style={{
              display: "flex", gap: 8, alignItems: "center",
              padding: "6px 8px", borderBottom: "1px solid #eee", fontSize: 12,
            }}>
              <span style={{
                background: t._kind === "overdue" ? "#fee2e2" : "#fef3c7",
                color:      t._kind === "overdue" ? "#991b1b" : "#92400e",
                padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
              }}>
                {t._kind === "overdue" ? `⏰ متأخر ${t.days_late}ي` : `⏸️ ${t.days_stuck}ي`}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "var(--brown-deep)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.title}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>
                  👤 {t.owner} · {t.status}
                </div>
              </div>
              <button type="button" onClick={() => nudgeOne(t.id)} disabled={nudgingId === t.id}
                data-testid={`mkt-perf-nudge-${t.id}`}
                style={{ padding: "3px 10px", background: "#25D366", color: "#fff",
                          border: "none", borderRadius: 4, fontSize: 11, fontWeight: 700,
                          cursor: nudgingId === t.id ? "wait" : "pointer" }}>
                {nudgingId === t.id ? "⏳" : "📱 تذكير"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Owner Leaderboard */}
      {data.by_owner?.length > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: "var(--brown-deep)", marginBottom: 8 }}>
            👥 توزيع المهام على الفريق
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 6 }}>
            {data.by_owner.map((o, idx) => (
              <div key={o.owner || idx} data-testid={`mkt-perf-owner-${idx}`} style={{
                background: "#FAFAFA", padding: 8, borderRadius: 6, fontSize: 11.5,
              }}>
                <div style={{ fontWeight: 700, color: "var(--brown-deep)" }}>{o.owner}</div>
                <div style={{ color: "var(--muted)", marginTop: 2 }}>
                  {o.done}/{o.total} منجزة ({o.completion_label ?? (o.completion_pct == null ? "بلا مهام" : `${o.completion_pct}%`)})
                  {o.overdue > 0 && ` · ⏰ ${o.overdue}`}
                  {o.stuck > 0 && ` · ⏸️ ${o.stuck}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Impact History (6 months) */}
      {history.length > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: "var(--brown-deep)" }}>
              📈 اتجاه الأثر آخر 6 أشهر
            </div>
            <a href={`${(process.env.REACT_APP_BACKEND_URL || '')}/api/marketing-plans/impact-history.xlsx?months=6`}
              target="_blank" rel="noreferrer"
              data-testid="mkt-perf-export-xlsx"
              style={{ padding: "4px 10px", background: "#059669", color: "#fff",
                        borderRadius: 4, fontSize: 11, fontWeight: 700, textDecoration: "none" }}>
              📊 تصدير Excel
            </a>
          </div>
          <div style={{ display: "flex", gap: 12, overflowX: "auto", padding: "4px 0" }}>
            {history.map((m) => (
              <div key={m.month_key} style={{ minWidth: 100, textAlign: "center" }}>
                <div style={{
                  height: 60, background: "#f5f5f5", borderRadius: 4, position: "relative",
                  display: "flex", alignItems: "flex-end", overflow: "hidden",
                }}>
                  {m.avg_impact_pct != null && (
                    <div style={{
                      width: "100%",
                      height: `${Math.min(m.avg_impact_pct, 100)}%`,
                      background: m.avg_impact_pct >= 80 ? "#10B981"
                                  : m.avg_impact_pct >= 50 ? "#C9A227" : "#EF4444",
                      transition: "height 300ms",
                    }} />
                  )}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--brown-deep)", marginTop: 3 }}>
                  {m.avg_impact_pct != null ? `${m.avg_impact_pct}%` : "—"}
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{m.label}</div>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{m.campaigns} حملة</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* v119.125 — Marketing Risk Radar (on-demand AI analysis) */}
      <div className="card" style={{ padding: 12, marginBottom: 12,
                                        background: "linear-gradient(135deg,#FBF7F0,#fff)",
                                        border: "1px solid #C9A227" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 13, color: "var(--brown-deep)" }}>
              🎯 رادار المخاطر التسويقية
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              تحليل Claude لأنماط: حملات بأثر منخفض · موظفون متأخرون · بنود مكلفة قليلة الإنجاز · تداخل بنود.
            </div>
          </div>
          <button type="button" onClick={runRadar} disabled={radarLoading}
            data-testid="mkt-perf-radar-run"
            style={{ padding: "8px 16px", background: "#3E2723", color: "#FBF7F0",
                      border: "none", borderRadius: 6, fontWeight: 800, fontSize: 12,
                      cursor: radarLoading ? "wait" : "pointer" }}>
            {radarLoading ? "⏳ يحلّل..." : "🤖 شغّل التحليل"}
          </button>
          {(me?.role === "admin" || me?.role === "ceo") && (
            <button type="button" onClick={sendRadarDigest} disabled={radarSending}
              data-testid="mkt-perf-radar-send-digest"
              style={{ padding: "8px 16px", background: "#25D366", color: "#fff",
                        border: "none", borderRadius: 6, fontWeight: 800, fontSize: 12,
                        marginInlineStart: 6,
                        cursor: radarSending ? "wait" : "pointer" }}>
              {radarSending ? "⏳ يرسل..." : "📤 أرسل ملخّص واتساب"}
            </button>
          )}
        </div>
        {radar && (
          <div data-testid="mkt-perf-radar-result">
            {radar.ai_summary && (
              <div style={{ padding: 10, background: "#fff", borderRadius: 6, marginBottom: 8, fontSize: 12.5, whiteSpace: "pre-wrap", color: "var(--brown-deep)" }}>
                {radar.ai_summary}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px,1fr))", gap: 6, marginBottom: 6 }}>
              <KpiTile testid="mkt-radar-drops" icon="📉" label="حملات بأثر منخفض"
                value={radar.signals?.impact_drop?.length || 0}
                tone={(radar.signals?.impact_drop?.length || 0) > 0 ? "danger" : "success"} />
              <KpiTile testid="mkt-radar-late" icon="⏰" label="موظفون متأخرون (Top)"
                value={radar.signals?.late_offenders?.length || 0}
                tone={(radar.signals?.late_offenders?.length || 0) > 0 ? "warn" : "success"} />
              <KpiTile testid="mkt-radar-budget" icon="💸" label="خطر ميزانية"
                value={radar.signals?.budget_risk?.length || 0}
                tone={(radar.signals?.budget_risk?.length || 0) > 0 ? "danger" : "success"} />
              <KpiTile testid="mkt-radar-buffer" icon="🚧" label="تداخل زمني"
                value={radar.signals?.buffer_risk?.length || 0}
                tone={(radar.signals?.buffer_risk?.length || 0) > 0 ? "warn" : "success"} />
            </div>
            {radar.signals?.late_offenders?.length > 0 && (
              <div style={{ fontSize: 11.5, color: "#92400e", background: "#fef3c7", padding: 6, borderRadius: 4, marginTop: 4 }}>
                🔻 أكثر تأخّراً: {radar.signals.late_offenders.map((o) => `${o.owner} (${o.incident_count})`).join("، ")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Unmeasured campaigns */}
      {data.unmeasured_campaigns?.length > 0 && (
        <div style={{ padding: 8, background: "#fef3c7", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
          ⚠️ {data.unmeasured_campaigns.length} حملة لم يُحدَّد لها مقياس أثر بعد. ادخل «الحملات → مهام» وأضف المقياس.
        </div>
      )}
    </div>
  );
}
